import { useEmpresasAlertas } from "../../lib/alertas/hooks";
import { CANAIS, SEVERIDADES, STATUSES } from "../../lib/alertas/tipos";

/*
 * Controles de filtro do historico de alertas (FE-7 — Tela 7 §10.5).
 *
 * Componente CONTROLADO e SEM ESTADO PROPRIO (espelha `FiltrosTrilha`): recebe os
 * valores correntes (`empresaId`/`severidade`/`canal`/`status`) e um callback
 * `aoMudarFiltro(campo, valor)`. O estado mora na PAGINA (`HistoricoAlertas.tsx`),
 * que ao receber a mudanca reseta o `offset`.
 *
 * REGRA DE VAZIO: cada select tem a opcao "Todas/Todos" (value `""`). A pagina e
 * quem decide NAO promover string vazia a filtro ativo (a camada de `api` ja omite
 * vazios da query). Aqui so refletimos o valor corrente como `""` quando ausente.
 *
 * A lista de empresas vem de `useEmpresasAlertas()` (escopo `configurar_alertas` do
 * usuario) — a UI nunca filtra empresa do lado do cliente. O rotulo e
 * `fantasia ?? razao_social`. Severidade/canal/status saem das tuplas `as const`
 * (fonte unica dos enums do backend), nunca strings soltas.
 */

/** Campos de filtro que este componente edita (limit/offset sao da pagina). */
export type CampoFiltro = "empresa_id" | "severidade" | "canal" | "status";

interface FiltrosAlertasProps {
  empresaId: string;
  severidade: string;
  canal: string;
  status: string;
  /** Notifica a pagina de uma mudanca em um campo de filtro. */
  aoMudarFiltro: (campo: CampoFiltro, valor: string) => void;
  /** Limpa todos os filtros. */
  aoLimpar: () => void;
}

export function FiltrosAlertas({
  empresaId,
  severidade,
  canal,
  status,
  aoMudarFiltro,
  aoLimpar,
}: FiltrosAlertasProps): JSX.Element {
  const empresas = useEmpresasAlertas();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1 font-corpo text-base text-off-black">
          <span className="font-subtitulo">Empresa</span>
          <select
            value={empresaId}
            onChange={(evento) =>
              aoMudarFiltro("empresa_id", evento.target.value)
            }
            className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
          >
            <option value="">Todas</option>
            {(empresas.data ?? []).map((empresa) => (
              <option key={empresa.id} value={empresa.id}>
                {empresa.fantasia ?? empresa.razao_social}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 font-corpo text-base text-off-black">
          <span className="font-subtitulo">Severidade</span>
          <select
            value={severidade}
            onChange={(evento) =>
              aoMudarFiltro("severidade", evento.target.value)
            }
            className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
          >
            <option value="">Todas</option>
            {SEVERIDADES.map(([valor, rotulo]) => (
              <option key={valor} value={valor}>
                {rotulo}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 font-corpo text-base text-off-black">
          <span className="font-subtitulo">Canal</span>
          <select
            value={canal}
            onChange={(evento) => aoMudarFiltro("canal", evento.target.value)}
            className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
          >
            <option value="">Todos</option>
            {CANAIS.map(([valor, rotulo]) => (
              <option key={valor} value={valor}>
                {rotulo}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 font-corpo text-base text-off-black">
          <span className="font-subtitulo">Status</span>
          <select
            value={status}
            onChange={(evento) => aoMudarFiltro("status", evento.target.value)}
            className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
          >
            <option value="">Todos</option>
            {STATUSES.map(([valor, rotulo]) => (
              <option key={valor} value={valor}>
                {rotulo}
              </option>
            ))}
          </select>
        </label>
      </div>

      <button
        type="button"
        onClick={aoLimpar}
        className="self-start rounded-md border border-platinum bg-platinum px-3 py-1 font-subtitulo text-off-black hover:text-flame"
      >
        Limpar filtros
      </button>
    </div>
  );
}
